/**
 * The six consents a pilot may need (PRD §5.4).
 *
 * Read-only here. Each consent is a real-world approval — a lender, a grid
 * operator, an HSE review — and toggling a checkbox is not how one is granted;
 * the record follows the approval rather than causing it. Editing belongs on
 * the pilot's edit form alongside the evidence, not as six switches someone can
 * flip while scrolling past.
 *
 * Rendered as `Gate` rows, which carry the met/not-met state in words as well
 * as in the disc (PRD §9.7). The count in the heading is the useful summary:
 * "4 of 6" is what someone asks before a kick-off.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.2, PRD §5.4, §9.7
 */
import { useTranslation } from 'react-i18next';

import type { PilotDetail } from '@nexus/contracts';

import { Card, CardBody, CardHead } from '../../../components/ui/Card';
import { Gate } from '../../../components/ui/Gate';
import { Kicker } from '../../../components/ui/Kicker';
import { Note } from '../../../components/ui/Note';

import { CONSENT_KEYS, type ConsentKey } from './consent-keys';

export interface ConsentChecklistProps {
  pilot: Pick<PilotDetail, ConsentKey>;
}

export function ConsentChecklist({ pilot }: ConsentChecklistProps): React.JSX.Element {
  const { t } = useTranslation();

  const granted = CONSENT_KEYS.filter((key) => pilot[key]).length;

  return (
    <Card>
      <CardHead>
        <Kicker as="h2">
          {t('pilot.consents.titleWithCount', {
            granted,
            total: CONSENT_KEYS.length,
          })}
        </Kicker>
      </CardHead>

      <CardBody>
        {CONSENT_KEYS.map((key) => (
          <Gate key={key} done={pilot[key]}>
            {t(`pilot.consents.${key}`)}
          </Gate>
        ))}

        {granted < CONSENT_KEYS.length && (
          <Note className="mt-s3">{t('pilot.consents.note')}</Note>
        )}
      </CardBody>
    </Card>
  );
}
